import React, { useState, useEffect } from 'react';
import Layout from '../components/dashboard/Layout';
import AnomalyDetectionPanel from '../components/AnomalyDetectionPanel';
import { financialAPI } from '../services/api';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Clock, 
  AlertCircle,
  Calendar,
  BarChart3,
  Send,
  Sparkles,
  RefreshCw
} from 'lucide-react';

const FinancialPageEnhanced = () => {
  const [dashboard, setDashboard] = useState(null);
  const [realtimeCashflow, setRealtimeCashflow] = useState(null);
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadFinancialData();
    // Auto refresh every 30 seconds
    const interval = setInterval(() => {
      loadFinancialData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadFinancialData = async () => {
    try {
      const [dashboardRes, cashflowRes, outstandingRes] = await Promise.all([
        financialAPI.getDashboard(),
        financialAPI.getRealtimeCashflow(),
        financialAPI.getOutstandingPayments()
      ]);
      setDashboard(dashboardRes.data);
      setRealtimeCashflow(cashflowRes.data);
      setOutstandingPayments(outstandingRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAIQuery = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    try {
      const response = await financialAPI.aiQuery({ query: aiQuery });
      setAiResponse(response.data);
    } catch (error) {
      console.error('AI query failed:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-500/10 text-red-500 border-red-500/30',
      high: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
      medium: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
      normal: 'bg-blue-500/10 text-blue-500 border-blue-500/30'
    };
    return colors[priority] || colors.normal;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <RefreshCw className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading financial data...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">💰 Financial Command Center</h1>
            <p className="text-muted-foreground">Real-time monitoring dengan AI Assistant</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Update: {lastUpdate.toLocaleTimeString('id-ID')}</span>
            <button
              onClick={loadFinancialData}
              className="ml-2 p-2 hover:bg-muted rounded-lg transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* AI Assistant */}
        <div className="glass-card p-6 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="flex items-center space-x-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">AI Financial Assistant</h3>
          </div>
          
          <form onSubmit={handleAIQuery} className="space-y-3">
            <div className="flex space-x-2">
              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="Tanya tentang keuangan... (contoh: 'Berapa total pendapatan bulan ini?')"
                className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
              />
              <button
                type="submit"
                disabled={aiLoading || !aiQuery.trim()}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {aiLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>Tanya</span>
              </button>
            </div>

            {aiResponse && (
              <div className="p-4 bg-background/50 rounded-lg border border-border">
                <p className="text-foreground font-medium mb-2">{aiResponse.answer}</p>
                {aiResponse.insights && aiResponse.insights.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {aiResponse.insights.map((insight, idx) => (
                      <p key={idx} className="text-sm text-muted-foreground flex items-start">
                        <span className="mr-2">💡</span>
                        <span>{insight}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Real-time Cash Flow */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <div className="flex items-center space-x-2 mb-6">
            <BarChart3 className="w-5 h-5 text-secondary" />
            <h3 className="text-lg font-semibold text-foreground">Cash Flow Real-time</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Daily */}
            <div className="p-5 rounded-xl border-2 border-secondary/30 bg-gradient-to-br from-secondary/5 to-transparent">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-muted-foreground">Harian (Hari Ini)</h4>
                <Calendar className="w-4 h-4 text-secondary" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Uang Masuk</p>
                  <p className="text-2xl font-bold text-secondary">
                    Rp {((realtimeCashflow?.daily?.income || 0) / 1000).toLocaleString('id-ID')}K
                  </p>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Uang Keluar</p>
                  <p className="text-lg font-semibold text-destructive">
                    Rp {((realtimeCashflow?.daily?.expense || 0) / 1000).toLocaleString('id-ID')}K
                  </p>
                </div>
                
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Net Cash Flow</p>
                  <p className={`text-xl font-bold ${(realtimeCashflow?.daily?.net || 0) >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                    Rp {((realtimeCashflow?.daily?.net || 0) / 1000).toLocaleString('id-ID')}K
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {realtimeCashflow?.daily?.transactions || 0} transaksi
                  </p>
                </div>
              </div>
            </div>

            {/* Weekly */}
            <div className="p-5 rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-muted-foreground">Mingguan (7 Hari)</h4>
                <Calendar className="w-4 h-4 text-primary" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Uang Masuk</p>
                  <p className="text-2xl font-bold text-primary">
                    Rp {((realtimeCashflow?.weekly?.income || 0) / 1000000).toFixed(1)}M
                  </p>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Uang Keluar</p>
                  <p className="text-lg font-semibold text-destructive">
                    Rp {((realtimeCashflow?.weekly?.expense || 0) / 1000).toLocaleString('id-ID')}K
                  </p>
                </div>
                
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Net Cash Flow</p>
                  <p className={`text-xl font-bold ${(realtimeCashflow?.weekly?.net || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                    Rp {((realtimeCashflow?.weekly?.net || 0) / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {realtimeCashflow?.weekly?.transactions || 0} transaksi
                  </p>
                </div>
              </div>
            </div>

            {/* Monthly */}
            <div className="p-5 rounded-xl border-2 border-foreground/30 bg-gradient-to-br from-foreground/5 to-transparent">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-muted-foreground">Bulanan (Bulan Ini)</h4>
                <Calendar className="w-4 h-4 text-foreground" />
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Uang Masuk</p>
                  <p className="text-2xl font-bold text-foreground">
                    Rp {((realtimeCashflow?.monthly?.income || 0) / 1000000).toFixed(1)}M
                  </p>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Uang Keluar</p>
                  <p className="text-lg font-semibold text-destructive">
                    Rp {((realtimeCashflow?.monthly?.expense || 0) / 1000000).toFixed(2)}M
                  </p>
                </div>
                
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Net Cash Flow</p>
                  <p className={`text-xl font-bold ${(realtimeCashflow?.monthly?.net || 0) >= 0 ? 'text-foreground' : 'text-destructive'}`}>
                    Rp {((realtimeCashflow?.monthly?.net || 0) / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {realtimeCashflow?.monthly?.transactions || 0} transaksi
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Outstanding Payments Detail */}
        <div className="glass-card p-6 rounded-xl border border-destructive/30 bg-gradient-to-br from-destructive/5 to-transparent">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <h3 className="text-lg font-semibold text-foreground">Pembayaran Belum Terealisasi</h3>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Piutang</p>
              <p className="text-2xl font-bold text-destructive">
                Rp {(outstandingPayments.reduce((sum, p) => sum + p.outstanding_amount, 0) / 1000000).toFixed(1)}M
              </p>
            </div>
          </div>

          {outstandingPayments.length === 0 ? (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-secondary mx-auto mb-3" />
              <p className="text-foreground font-medium">Hebat! Tidak ada piutang yang tertunda</p>
              <p className="text-sm text-muted-foreground mt-1">Semua invoice sudah terbayar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {outstandingPayments.map((payment) => (
                <div
                  key={payment.invoice_id}
                  className="p-4 rounded-lg border border-border bg-background/50 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    {/* Left Side - Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-foreground">{payment.client_name}</p>
                          <p className="text-sm text-muted-foreground">Invoice: {payment.invoice_number}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(payment.priority)}`}>
                          {payment.priority}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Total Invoice</p>
                          <p className="font-medium text-foreground">
                            Rp {(payment.total_amount / 1000).toLocaleString('id-ID')}K
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Sudah Dibayar</p>
                          <p className="font-medium text-secondary">
                            Rp {(payment.paid_amount / 1000).toLocaleString('id-ID')}K
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Sisa Tagihan</p>
                          <p className="font-bold text-destructive">
                            Rp {(payment.outstanding_amount / 1000).toLocaleString('id-ID')}K
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Jatuh Tempo</p>
                          <p className="font-medium text-foreground">
                            {new Date(payment.due_date).toLocaleDateString('id-ID')}
                          </p>
                        </div>
                      </div>

                      {payment.days_overdue > 0 && (
                        <div className="mt-2 flex items-center space-x-1 text-xs text-destructive">
                          <Clock className="w-3 h-3" />
                          <span>Terlambat {payment.days_overdue} hari</span>
                        </div>
                      )}

                      <div className="mt-2 text-xs text-muted-foreground">
                        <span>📧 {payment.client_email}</span>
                        <span className="ml-3">📱 {payment.client_phone}</span>
                      </div>
                    </div>

                    {/* Right Side - Action Hint */}
                    <div className="flex items-center space-x-2">
                      {payment.days_overdue > 30 ? (
                        <div className="text-xs text-destructive font-medium bg-destructive/10 px-3 py-2 rounded">
                          ⚠️ Segera Follow-up!
                        </div>
                      ) : payment.days_overdue > 7 ? (
                        <div className="text-xs text-orange-500 font-medium bg-orange-500/10 px-3 py-2 rounded">
                          📞 Perlu Reminder
                        </div>
                      ) : payment.days_overdue > 0 ? (
                        <div className="text-xs text-yellow-600 font-medium bg-yellow-500/10 px-3 py-2 rounded">
                          ⏰ Monitoring
                        </div>
                      ) : (
                        <div className="text-xs text-blue-600 font-medium bg-blue-500/10 px-3 py-2 rounded">
                          ✓ On Track
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Insights Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-secondary/10">
                <TrendingUp className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Terealisasi (Bulan Ini)</p>
                <p className="text-xl font-bold text-secondary">
                  Rp {((realtimeCashflow?.monthly?.income || 0) / 1000000).toFixed(1)}M
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-destructive/10">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Belum Terealisasi</p>
                <p className="text-xl font-bold text-destructive">
                  Rp {(outstandingPayments.reduce((sum, p) => sum + p.outstanding_amount, 0) / 1000000).toFixed(1)}M
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cash Position</p>
                <p className="text-xl font-bold text-primary">
                  Rp {((dashboard?.cash_position || 0) / 1000000).toFixed(1)}M
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* AI Anomaly Detection */}
        <AnomalyDetectionPanel />
      </div>
    </Layout>
  );
};

export default FinancialPageEnhanced;
